// Sharing (contracts/index-cards.md §5, admin-api.md "Sharing"): Logic Index and daily shares, link clicks, app
// opens from links, the top shares and sharers, and the index population; one share's snapshot and its clicks.
// Every role reads. The index.* / share.* settings go through GET /config and PATCH /config (admin). Kept apart from
// client.ts / queries.ts like the other feature modules. Nullable fields default to null when absent.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api, request } from "./client";
import { keys } from "./queries";
import type { JsonValue } from "./schemas";

const count = z.number().int().min(0);
/** 0–1 shares; opens ÷ clicks can pass 1 (a universal link opens the app without a web click). */
const ratio = z.number().min(0);
const nil = <T extends z.ZodType>(schema: T) => schema.nullable().default(null);

export const SHARE_KINDS = ["index", "daily"] as const;
export const ShareKind = z.enum(SHARE_KINDS);
export type ShareKind = z.infer<typeof ShareKind>;

/** `handle` null: `name` is the generated "Lumen #4F2A". */
export const ShareOwner = z.object({ userId: z.string(), handle: nil(z.string()), name: z.string() });
export type ShareOwner = z.infer<typeof ShareOwner>;

// ---------------------------------------------------------------- GET /sharing

export const SharingDay = z.object({ date: z.string(), sharesCreated: count, sharesCompleted: count, clicks: count, uniques: count, previews: count, opens: count });
export type SharingDay = z.infer<typeof SharingDay>;

const ShareCounts = z.object({ key: z.string(), created: count, completed: count });
const ClickCounts = z.object({ key: z.string(), clicks: count, uniques: count, previews: count });
export type ClickCounts = z.infer<typeof ClickCounts>;

export const TopShare = z.object({
  kind: ShareKind,
  ref: z.string(),
  url: z.string(),
  /** null once the player is gone. */
  owner: nil(ShareOwner),
  /** Index shares: the value and tier; daily shares: the puzzle number. */
  value: nil(z.number()),
  tier: nil(z.string()),
  puzzleNumber: nil(z.number().int()),
  format: nil(z.string()),
  channel: nil(z.string()),
  clicks: count,
  uniques: count,
  previews: count,
  opens: count,
  createdAt: z.string(),
});
export type TopShare = z.infer<typeof TopShare>;

export const TopSharer = z.object({ userId: z.string(), handle: nil(z.string()), name: z.string(), shares: count, completed: count, clicks: count, opens: count });
export type TopSharer = z.infer<typeof TopSharer>;

/** The current population (§1), whatever the range: 14 bins and 6 tiers. */
export const Population = z.object({
  holders: count,
  minPlayers: z.number().int(),
  /** holders ≥ minPlayers: players see their standing and the curve. */
  enough: z.boolean(),
  computedAt: nil(z.string()),
  bins: z.array(z.object({ from: z.number(), to: z.number(), count, share: ratio })),
  tiers: z.array(z.object({ id: z.string(), min: z.number(), max: z.number(), count, share: ratio })),
});
export type Population = z.infer<typeof Population>;

export const Sharing = z.object({
  from: z.string(),
  to: z.string(),
  totals: z.object({
    sharesCreated: count,
    sharesCompleted: count,
    clicks: count,
    uniques: count,
    previews: count,
    bots: count,
    opens: count,
    opensPerClick: nil(ratio),
  }),
  days: z.array(SharingDay),
  shares: z.object({
    byKind: z.array(ShareCounts).default([]),
    byFormat: z.array(ShareCounts).default([]),
    byChannel: z.array(z.object({ key: z.string(), completed: count })).default([]),
  }),
  clicks: z.object({
    byKind: z.array(ClickCounts).default([]),
    byPlatform: z.array(ClickCounts).default([]),
    byInApp: z.array(ClickCounts).default([]),
    bySource: z.array(ClickCounts).default([]),
  }),
  topShares: z.array(TopShare),
  topSharers: z.array(TopSharer),
  population: Population,
});
export type Sharing = z.infer<typeof Sharing>;

// ---------------------------------------------------------------- GET /sharing/shares/{kind}/{ref}

/** The snapshot stored when the share was created; fields that don't apply to the kind are null. */
export const ShareSnapshot = z.object({
  kind: ShareKind,
  ref: z.string(),
  url: z.string(),
  owner: nil(ShareOwner),
  createdAt: z.string(),
  completedAt: nil(z.string()),
  format: nil(z.string()),
  channel: nil(z.string()),
  locale: nil(z.string()),
  showHandle: nil(z.boolean()),
  // index shares
  value: nil(z.number()),
  tier: nil(z.string()),
  betterThan: nil(ratio),
  players: nil(count),
  tierShare: nil(ratio),
  strongest: nil(z.object({ category: z.string(), betterThan: ratio })),
  streak: nil(count),
  /** The player's glow: an orb index 0–5. */
  glow: nil(z.number().int()),
  // daily shares
  puzzleNumber: nil(z.number().int()),
  mode: nil(z.enum(["daily", "hard"])),
  correct: nil(z.boolean()),
  /** 0–100. */
  percentile: nil(z.number().min(0).max(100)),
  rank: nil(z.number().int()),
});
export type ShareSnapshot = z.infer<typeof ShareSnapshot>;

const KeyClicks = z.object({ key: z.string(), clicks: count, uniques: count });
export type KeyClicks = z.infer<typeof KeyClicks>;

export const ShareDetail = z.object({
  share: ShareSnapshot,
  from: z.string(),
  to: z.string(),
  totals: z.object({ clicks: count, uniques: count, previews: count, bots: count, opens: count, opensPerClick: nil(ratio) }),
  days: z.array(z.object({ date: z.string(), clicks: count, uniques: count, previews: count, opens: count })),
  sources: z.array(KeyClicks).default([]),
  platforms: z.array(KeyClicks).default([]),
  inApp: z.array(KeyClicks).default([]),
});
export type ShareDetail = z.infer<typeof ShareDetail>;

// ---------------------------------------------------------------- client and hooks

export interface SharingRange {
  from?: string;
  to?: string;
}

const enc = encodeURIComponent;

export const sharingApi = {
  /** UTC days, `to` inclusive; the API defaults to the last 28 days and allows at most 180. */
  overview: (range: SharingRange, signal?: AbortSignal) => request(Sharing, "GET", "/sharing", { query: { ...range }, signal }),
  /** The API defaults to the share's creation day through today. 404 not_found for an unknown share. */
  share: (kind: string, ref: string, range: SharingRange = {}, signal?: AbortSignal) =>
    request(ShareDetail, "GET", `/sharing/shares/${enc(kind)}/${enc(ref)}`, { query: { ...range }, signal }),
};

export const sharingKeys = {
  all: ["sharing"] as const,
  overview: (r: SharingRange) => ["sharing", "overview", r.from ?? "", r.to ?? ""] as const,
  share: (kind: string, ref: string, r: SharingRange) => ["sharing", "share", kind, ref, r.from ?? "", r.to ?? ""] as const,
};

export const useSharing = (range: SharingRange, enabled = true) =>
  useQuery({ queryKey: sharingKeys.overview(range), queryFn: ({ signal }) => sharingApi.overview(range, signal), placeholderData: keepPreviousData, enabled });

export const useShare = (kind: string, ref: string, range: SharingRange = {}) =>
  useQuery({ queryKey: sharingKeys.share(kind, ref, range), queryFn: ({ signal }) => sharingApi.share(kind, ref, range, signal), enabled: Boolean(kind && ref) });

/** PATCH /config with the index.* / share.* keys (admin, audited); the population's minPlayers changes with them. */
export function useSaveSharingConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: Record<string, JsonValue>) => api.patchConfig(values),
    onSuccess: () =>
      Promise.all([qc.invalidateQueries({ queryKey: keys.config }), qc.invalidateQueries({ queryKey: keys.audit }), qc.invalidateQueries({ queryKey: sharingKeys.all })]),
  });
}
